import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  // ── Page header ────────────────────────────────────────────────────────────
  pageTitle: {
    id: 'course-authoring.lti-tools.page.title',
    defaultMessage: 'LTI Tools',
    description: 'Heading displayed at the top of the LTI Tools tab',
  },
  pageDescription: {
    id: 'course-authoring.lti-tools.page.description',
    defaultMessage:
      'Create and manage LTI tool configurations. '
      + 'Each configuration generates a set of custom parameters to paste into an Open edX Studio LTI unit.',
  },

  // ── Tool selector ───────────────────────────────────────────────────────────
  toolSelectorLabel: {
    id: 'course-authoring.lti-tools.tool-selector.label',
    defaultMessage: 'Select an LTI Tool',
    description: 'Label for the tool type dropdown at the top of the LTI Tools tab',
  },
  toolSelectorPlaceholder: {
    id: 'course-authoring.lti-tools.tool-selector.placeholder',
    defaultMessage: '— Choose a tool type —',
  },
  toolSkillEvaluatorLabel: {
    id: 'course-authoring.lti-tools.tool.skill-evaluator.label',
    defaultMessage: 'Skill Evaluator',
  },
  toolSkillEvaluatorDescription: {
    id: 'course-authoring.lti-tools.tool.skill-evaluator.description',
    defaultMessage:
      'The Skill Evaluator measures and develops learner proficiency in the workplace skills a course '
      + 'teaches. Skills and rubrics come directly from the course — each major learning subsection '
      + 'defines one skill dimension along which learner progress will be evaluated, with a targeted '
      + 'sub-rubric grounded in that section\'s content.\n\n'
      + 'A Pre-Assessment unit at the start of the course establishes a learner\'s baseline. The learner '
      + 'reads a workplace scenario covering all course skills and writes a response, receiving '
      + 'AI-powered dimension-level scores immediately.\n\n'
      + 'A Check-in unit placed after each learning subsection assesses that section\'s specific skill '
      + 'using a targeted sub-rubric based on the concepts and tools discussed in that subsection\'s '
      + 'learning material. Learners get formative feedback and can ask follow-up questions answered '
      + 'from the actual course content. Learners may re-attempt a number of times (this is configured in the setup wizard).\n\n'
      + 'A Post-Assessment unit at the end uses the same scenario as the pre-assessment — producing a '
      + 'final score that, together with the check-in history, shows measurable skill progression. '
      + 'All scores are passed back to the Open edX gradebook automatically.\n\n'
      + 'A Report LTI unit displays their final improvement along the skill dimensions.\n\n'
      + 'Use the AI Setup Wizard to automatically generate the full rubric and scenario set from your '
      + 'course content in two guided steps. You are free to make any adjustments necessary before '
      + 'saving the configuration.\n\n'
      + 'You may search for an existing configuration to make changes, or make a new one.',
  },
  toolStudioSetupHint: {
    id: 'course-authoring.lti-tools.tool.studio-setup-hint',
    defaultMessage: 'Studio prerequisite: In Course Settings → Advanced Settings, add `lti_consumer` to `advanced_modules` and add the passport `phoenix-skill-eval:phoenix-skill-eval:<LTI_SECRET_HERE>`. The secret must match the Phoenix Assistant environment. Then add each unit through Add Component → Advanced → LTI.',
  },

  // ── Course ID input ─────────────────────────────────────────────────────────
  courseIdLabel: {
    id: 'course-authoring.lti-tools.course-id.label',
    defaultMessage: 'Course ID',
  },
  courseIdPlaceholder: {
    id: 'course-authoring.lti-tools.course-id.placeholder',
    defaultMessage: 'e.g. course-v1:UOPX+COMM101+2026',
  },
  loadConfigsButton: {
    id: 'course-authoring.lti-tools.load-configs.button',
    defaultMessage: 'Load Configs',
  },

  // ── AI Wizard ───────────────────────────────────────────────────────────────
  wizardLaunchButton: {
    id: 'course-authoring.lti-tools.wizard.launch-button',
    defaultMessage: '✨ AI Setup Wizard',
    description: 'Button that opens the AI-guided configuration wizard',
  },
  wizardHeading: {
    id: 'course-authoring.lti-tools.wizard.heading',
    defaultMessage: 'AI Setup Wizard',
  },
  wizardDescription: {
    id: 'course-authoring.lti-tools.wizard.description',
    defaultMessage:
      'The wizard analyses your course content and automatically generates a full set of '
      + 'LTI configurations — skill rubrics, check-in scenarios, and assessment scenarios — '
      + 'which you can review and edit before saving.',
  },

  // Step 1
  wizardStep1Heading: {
    id: 'course-authoring.lti-tools.wizard.step1.heading',
    defaultMessage: 'Step 1 — Analyse Course Structure',
  },
  wizardStep1Description: {
    id: 'course-authoring.lti-tools.wizard.step1.description',
    defaultMessage:
      'The AI will read your course\'s cached unit summaries, identify each learning section, '
      + 'and propose a skill name and scoring rubric for each one. '
      + 'You can edit any skill name or rubric dimension before moving on.',
  },
  wizardAnalyseButton: {
    id: 'course-authoring.lti-tools.wizard.analyse-button',
    defaultMessage: 'Analyse Course',
  },
  wizardAnalysingMessage: {
    id: 'course-authoring.lti-tools.wizard.analysing',
    defaultMessage: 'Analysing course structure… (this may take up to 60 seconds)',
  },
  wizardStep1ResultHeading: {
    id: 'course-authoring.lti-tools.wizard.step1-result.heading',
    defaultMessage: 'Course Structure — Review & Edit',
  },
  wizardCourseSkillLabel: {
    id: 'course-authoring.lti-tools.wizard.course-skill.label',
    defaultMessage: 'Overall Course Skill Name',
  },
  wizardCourseSkillHelp: {
    id: 'course-authoring.lti-tools.wizard.course-skill.help',
    defaultMessage: 'Used as the skill name on the pre- and post-assessment configs.',
  },
  wizardSectionSkillLabel: {
    id: 'course-authoring.lti-tools.wizard.section-skill.label',
    defaultMessage: 'Skill Dimension',
  },
  wizardPlacementHintLabel: {
    id: 'course-authoring.lti-tools.wizard.placement-hint.label',
    defaultMessage: 'Placement',
  },
  wizardSubRubricHeading: {
    id: 'course-authoring.lti-tools.wizard.sub-rubric.heading',
    defaultMessage: 'Check-in Rubric Dimensions',
  },

  // Step 2
  wizardStep2Heading: {
    id: 'course-authoring.lti-tools.wizard.step2.heading',
    defaultMessage: 'Step 2 — Generate Scenarios',
  },
  wizardStep2Description: {
    id: 'course-authoring.lti-tools.wizard.step2.description',
    defaultMessage:
      'The AI will generate a realistic workplace scenario for each check-in unit '
      + 'and one shared scenario for the pre- and post-assessment. '
      + 'All scenarios are editable before saving.',
  },
  wizardGeneratingMessage: {
    id: 'course-authoring.lti-tools.wizard.generating',
    defaultMessage: 'Generating scenarios in parallel… (this may take up to 45 seconds)',
  },
  wizardStep2ResultHeading: {
    id: 'course-authoring.lti-tools.wizard.step2-result.heading',
    defaultMessage: 'Scenarios — Review & Edit',
  },
  wizardAssessmentScenarioHeading: {
    id: 'course-authoring.lti-tools.wizard.assessment-scenario.heading',
    defaultMessage: 'Pre/Post Assessment Scenario (shared)',
  },
  wizardAssessmentScenarioHelp: {
    id: 'course-authoring.lti-tools.wizard.assessment-scenario.help',
    defaultMessage:
      'This same scenario is used for both the pre-assessment (baseline) and post-assessment (final measure). '
      + 'Using identical scenarios ensures the skill delta is meaningful.',
  },
  wizardCheckinScenariosHeading: {
    id: 'course-authoring.lti-tools.wizard.checkin-scenarios.heading',
    defaultMessage: 'Check-in Scenarios (one per section)',
  },
  wizardScenarioTextLabel: {
    id: 'course-authoring.lti-tools.wizard.scenario-text.label',
    defaultMessage: 'Scenario Text',
  },
  wizardResponsePromptLabel: {
    id: 'course-authoring.lti-tools.wizard.response-prompt.label',
    defaultMessage: 'Response Prompt',
  },
  wizardMaxAttemptsLabel: {
    id: 'course-authoring.lti-tools.wizard.max-attempts.label',
    defaultMessage: 'Check-in Max Attempts',
  },
  wizardMaxAttemptsHelp: {
    id: 'course-authoring.lti-tools.wizard.max-attempts.help',
    defaultMessage: 'Applied to all check-in units. Assessments are always 1 attempt.',
  },

  // Step 3 — Save
  wizardSaveButton: {
    id: 'course-authoring.lti-tools.wizard.save-button',
    defaultMessage: 'Save All Configurations',
  },
  wizardSavingMessage: {
    id: 'course-authoring.lti-tools.wizard.saving',
    defaultMessage: 'Saving configurations…',
  },
  wizardSaveSuccessMessage: {
    id: 'course-authoring.lti-tools.wizard.save-success',
    defaultMessage: 'All configurations saved successfully. See the table below for Studio setup.',
  },
  wizardSaveErrorMessage: {
    id: 'course-authoring.lti-tools.wizard.save-error',
    defaultMessage: 'Failed to save one or more configurations. Please check the form and try again.',
  },
  wizardCancelButton: {
    id: 'course-authoring.lti-tools.wizard.cancel-button',
    defaultMessage: 'Cancel',
  },
  wizardBackButton: {
    id: 'course-authoring.lti-tools.wizard.back-button',
    defaultMessage: '← Back',
  },
  wizardNextButton: {
    id: 'course-authoring.lti-tools.wizard.next-button',
    defaultMessage: 'Next →',
  },
  wizardAnalysisErrorMessage: {
    id: 'course-authoring.lti-tools.wizard.analysis-error',
    defaultMessage:
      'Course analysis failed. Make sure this course has been ingested and has cached content summaries.',
  },
  wizardScenariosErrorMessage: {
    id: 'course-authoring.lti-tools.wizard.scenarios-error',
    defaultMessage: 'Scenario generation failed. Please try again.',
  },

  // ── Config list ─────────────────────────────────────────────────────────────
  configListEmptyMessage: {
    id: 'course-authoring.lti-tools.config-list.empty',
    defaultMessage: 'No configurations found for this course. Use the AI Setup Wizard to get started.',
  },
  configListErrorMessage: {
    id: 'course-authoring.lti-tools.config-list.error',
    defaultMessage: 'Failed to load configurations. Please try again.',
  },
  columnSkill: {
    id: 'course-authoring.lti-tools.config-list.column.skill',
    defaultMessage: 'Skill',
  },
  columnType: {
    id: 'course-authoring.lti-tools.config-list.column.type',
    defaultMessage: 'Type',
  },
  columnScenarioId: {
    id: 'course-authoring.lti-tools.config-list.column.scenario-id',
    defaultMessage: 'Scenario ID',
  },
  columnLastUpdated: {
    id: 'course-authoring.lti-tools.config-list.column.last-updated',
    defaultMessage: 'Last Updated',
  },
  columnActions: {
    id: 'course-authoring.lti-tools.config-list.column.actions',
    defaultMessage: 'Actions',
  },
  deleteButton: {
    id: 'course-authoring.lti-tools.config-list.delete-button',
    defaultMessage: 'Delete',
  },
  studioBlockButton: {
    id: 'course-authoring.lti-tools.config-list.studio-block-button',
    defaultMessage: 'Studio Config',
  },
  deleteConfirmMessage: {
    id: 'course-authoring.lti-tools.delete-confirm',
    defaultMessage: 'Are you sure you want to delete this configuration? This cannot be undone.',
  },
  deleteErrorMessage: {
    id: 'course-authoring.lti-tools.delete-error',
    defaultMessage: 'Failed to delete configuration. Please try again.',
  },
  resetConfigurationsButton: {
    id: 'course-authoring.lti-tools.config-list.reset.button',
    defaultMessage: 'Delete all configurations and start over',
  },
  resetConfigurationsConfirmMessage: {
    id: 'course-authoring.lti-tools.config-list.reset.confirm',
    defaultMessage: 'Delete all Skill Evaluator configurations for this course? Existing Studio LTI blocks will retain invalid configuration IDs until they are replaced. This cannot be undone.',
  },
  resetConfigurationsErrorMessage: {
    id: 'course-authoring.lti-tools.config-list.reset.error',
    defaultMessage: 'Failed to delete all configurations. Please try again.',
  },

  // ── Studio copy-paste panel ──────────────────────────────────────────────────
  studioPanelHeading: {
    id: 'course-authoring.lti-tools.studio-panel.heading',
    defaultMessage: 'Studio LTI Configuration',
  },
  studioPanelDescription: {
    id: 'course-authoring.lti-tools.studio-panel.description',
    defaultMessage: 'Paste these values into the LTI Advanced component in Open edX Studio.',
  },
  studioPanelToolUrlLabel: {
    id: 'course-authoring.lti-tools.studio-panel.tool-url.label',
    defaultMessage: 'Tool URL',
  },
  studioPanelConsumerKeyLabel: {
    id: 'course-authoring.lti-tools.studio-panel.consumer-key.label',
    defaultMessage: 'LTI ID / Consumer Key',
  },
  studioPanelCustomParamsLabel: {
    id: 'course-authoring.lti-tools.studio-panel.custom-params.label',
    defaultMessage: 'Custom Parameters',
  },
  studioPanelCopyButton: {
    id: 'course-authoring.lti-tools.studio-panel.copy.button',
    defaultMessage: 'Copy',
  },
  studioPanelCopiedButton: {
    id: 'course-authoring.lti-tools.studio-panel.copied.button',
    defaultMessage: 'Copied!',
  },
  studioPanelCloseButton: {
    id: 'course-authoring.lti-tools.studio-panel.close.button',
    defaultMessage: 'Close',
  },
  studioPanelErrorMessage: {
    id: 'course-authoring.lti-tools.studio-panel.error',
    defaultMessage: 'Failed to load Studio configuration. Please try again.',
  },

  // ── Progress Report section (inside Studio panel) ───────────────────────────
  reportSectionHeading: {
    id: 'course-authoring.lti-tools.studio-panel.report.heading',
    defaultMessage: 'Progress Report Tool',
  },
  reportSectionDescription: {
    id: 'course-authoring.lti-tools.studio-panel.report.description',
    defaultMessage:
      'Add this as a separate LTI unit at the end of the course. '
      + 'When a learner opens it, they see a full skill progression report — '
      + 'pre-assessment baseline, check-in scores over time, and post-assessment result — '
      + 'grouped by skill. No custom parameters are needed; Open edX sends the course ID automatically.',
  },
  reportSectionToolUrlLabel: {
    id: 'course-authoring.lti-tools.studio-panel.report.tool-url.label',
    defaultMessage: 'Report Tool URL',
  },
  reportSectionNoParams: {
    id: 'course-authoring.lti-tools.studio-panel.report.no-params',
    defaultMessage:
      'Use the same Consumer Key and Shared Secret shown above. '
      + 'No custom parameters are required — Open edX sends the course ID automatically on every launch.',
  },
  reportSectionCopyButton: {
    id: 'course-authoring.lti-tools.studio-panel.report.copy.button',
    defaultMessage: 'Copy Report URL',
  },
  reportSectionCopiedButton: {
    id: 'course-authoring.lti-tools.studio-panel.report.copied.button',
    defaultMessage: 'Copied!',
  },

  // ── Edit config panel ────────────────────────────────────────────────────────
  editButton: {
    id: 'course-authoring.lti-tools.config-list.edit-button',
    defaultMessage: 'Edit',
  },
  editPanelHeading: {
    id: 'course-authoring.lti-tools.edit-panel.heading',
    defaultMessage: 'Edit Configuration',
  },
  editPanelDescription: {
    id: 'course-authoring.lti-tools.edit-panel.description',
    defaultMessage: 'Update the skill name, scenario, response prompt, rubric dimensions, or max attempts. Changes are saved immediately.',
  },
  editSkillLabel: {
    id: 'course-authoring.lti-tools.edit-panel.skill.label',
    defaultMessage: 'Skill Dimension',
  },
  editScenarioTextLabel: {
    id: 'course-authoring.lti-tools.edit-panel.scenario-text.label',
    defaultMessage: 'Scenario Text',
  },
  editResponsePromptLabel: {
    id: 'course-authoring.lti-tools.edit-panel.response-prompt.label',
    defaultMessage: 'Response Prompt',
  },
  editMaxAttemptsLabel: {
    id: 'course-authoring.lti-tools.edit-panel.max-attempts.label',
    defaultMessage: 'Max Attempts',
  },
  editRubricHeading: {
    id: 'course-authoring.lti-tools.edit-panel.rubric.heading',
    defaultMessage: 'Grading Rubric Dimensions',
  },
  editRubricNamePlaceholder: {
    id: 'course-authoring.lti-tools.edit-panel.rubric.name.placeholder',
    defaultMessage: 'Dimension name',
  },
  editSaveButton: {
    id: 'course-authoring.lti-tools.edit-panel.save.button',
    defaultMessage: 'Save Changes',
  },
  editCancelButton: {
    id: 'course-authoring.lti-tools.edit-panel.cancel.button',
    defaultMessage: 'Cancel',
  },
  editSaveSuccessMessage: {
    id: 'course-authoring.lti-tools.edit-panel.save-success',
    defaultMessage: 'Configuration updated successfully.',
  },
  editSaveErrorMessage: {
    id: 'course-authoring.lti-tools.edit-panel.save-error',
    defaultMessage: 'Failed to save changes. Please try again.',
  },
  postAssessmentReadOnlyHint: {
    id: 'course-authoring.lti-tools.edit-panel.post-assessment-read-only-hint',
    defaultMessage: 'This post-assessment automatically mirrors its matching pre-assessment to preserve a meaningful baseline-to-final comparison. To make changes, edit the matching pre-assessment.',
  },
});

export default messages;
